import { inject } from '@angular/core'
import { tapResponse } from '@ngrx/operators'
import { signalStoreFeature, type, withFeature, withMethods, withState } from '@ngrx/signals'
import { Events, injectDispatch, on, withEventHandlers, withReducer } from '@ngrx/signals/events'
import { filter, map, mergeMap, takeUntil, tap } from 'rxjs'

import { isPaymentRequired } from '@app/core/is-payment-required'
import { nonCancelableRequest } from '@app/core/rxjs/non-cancelable-request'
import { CreditsStore } from '@app/modules/credits/store/credits.store'
import type { VisitReason } from '@app/modules/navigation/services/credits-navigation-params.service'
import { CreditsNavigationService } from '@app/modules/navigation/services/credits-navigation.service'
import { PopoverService } from '@app/overlays/popover/popover.service'
import { AccessService } from '@app/services/access.service'

import { conversationEvents } from './conversation.events'
import type { ConversationState } from './conversation.store'
import { CONVERSATION_STORE_CONFIG } from './conversation.store'
import { injectDefaultErrorHandler } from './default-error-handler'
import { sendingEvents } from './sending.events'
import { sliceAffordableMessages } from './sending.feature.utils'
import type { SendMessagesPayload } from '../../models/api.model'
import type { Conversation } from '../../models/conversation.model'
import type { Message } from '../../models/message.model'
import { photoUploadFeature } from '../../modules/message-photo/store/photo-upload.feature'
import { voiceSendingFeature } from '../../modules/voice-message/store/voice-sending.feature'
import { ConversationUIService } from '../../services/conversation-ui.service'
import { ConversationService } from '../../services/conversation.service'
import { MessageCacheService } from '../../services/message-cache.service'
import { MessagingService } from '../../services/messaging.service'

interface SendingFeatureState {
  sendInProgress: boolean
}

const initialState: SendingFeatureState = {
  sendInProgress: false,
}

export const sendingFeature = () =>
  signalStoreFeature(
    { state: type<ConversationState>() },
    withState(initialState),
    withFeature(() => photoUploadFeature()),
    withFeature(() => voiceSendingFeature()),
    withMethods(
      (
        store,
        conversationUIService = inject(ConversationUIService),
        creditsNavigationService = inject(CreditsNavigationService),
        dispatch = injectDispatch(sendingEvents),
        messagingService = inject(MessagingService),
        source = inject(CONVERSATION_STORE_CONFIG).source,
      ) => ({
        attemptToSendMessage: (conversation: Conversation, payload: SendMessagesPayload, fakeMessages: Message[]) => {
          const canAffordWithoutBoost = messagingService.canAffordMessageSend(conversation, payload, false)
          const hasPhotos = payload.messages.some(message => message.type === 'photo')
          const hasVoiceMessages = payload.messages.some(message => message.type === 'voice')

          const visitCredits = () => {
            let reason: VisitReason = 'sendMessage'

            if (hasPhotos) {
              reason = 'sendMessagePhoto'
            }

            if (hasVoiceMessages) {
              reason = 'sendVoiceMessage'
            }

            return creditsNavigationService.visitCredits(reason, {
              source: store.isSuggestedProfile() ? 'messages-page-suggestions' : source,
              profile_id: conversation.profile.id,
            })
          }

          if (!canAffordWithoutBoost) {
            if (hasPhotos) {
              const affordablePhotosCount = messagingService.affordableMessagePhotosCount(conversation)
              const instance = conversationUIService.openMessagePhotoAlbumInsufficientCreditsModal()
              instance.affordablePhotosCount = affordablePhotosCount
              instance.mode = 'send'
              instance.onConfirm(action => {
                if (action === 'Buy Credits') {
                  visitCredits()
                } else {
                  dispatch.sendMessage({
                    payload: { ...payload, messages: sliceAffordableMessages(affordablePhotosCount, payload.messages) },
                    fakeMessages: sliceAffordableMessages(affordablePhotosCount, fakeMessages),
                    conversation: store.conversation()!,
                  })
                }
              })
              return
            } else {
              return visitCredits()
            }
          }

          const canAffordWithBoost = messagingService.canAffordMessageSend(conversation, payload, true)
          if (!canAffordWithBoost) {
            const instance = hasVoiceMessages
              ? conversationUIService.openVoiceMessageBoostInsufficientCreditsModalComponent()
              : conversationUIService.openBoostMessageInsufficientCreditsModal(conversation.profile)
            instance.onConfirm(() => visitCredits())
          }

          dispatch.sendMessage({ payload, fakeMessages, conversation })
        },
      }),
    ),
    withReducer(
      on(sendingEvents.sendMessage, () => ({ sendInProgress: true })),
      on(sendingEvents.sendMessageEnd, () => ({ sendInProgress: false })),
    ),
    withEventHandlers(
      (
        store,
        conversationService = inject(ConversationService),
        creditsStore = inject(CreditsStore),
        creditsNavigationService = inject(CreditsNavigationService),
        dispatch = injectDispatch(sendingEvents),
        messagingService = inject(MessagingService),
        source = inject(CONVERSATION_STORE_CONFIG).source,
        accessService = inject(AccessService),
        messageCacheService = inject(MessageCacheService),
        events = inject(Events),
        popoverService = inject(PopoverService),
        defaultErrorHandler = injectDefaultErrorHandler(),
      ) => ({
        sendMessage$: events.on(sendingEvents.sendMessage).pipe(
          map(({ payload }) => payload),
          mergeMap(({ conversation, payload }) =>
            conversationService
              .sendMessages(
                conversation.profile.browse_id,
                payload,
                store.isSuggestedProfile() ? 'messages-page-suggestions' : source,
              )
              .pipe(
                tap(response => creditsStore.onCreditsSpent(response)),
                nonCancelableRequest(),
                tapResponse({
                  next: response =>
                    dispatch.sendRealMessageSuccess({
                      response,
                      payload,
                      profile_id: conversation.profile.id,
                      hadFake: conversation.allow_instant_messages,
                    }),
                  error: error => dispatch.sendMessageFailure({ error, payload, profile_id: conversation.profile.id }),
                  finalize: () => dispatch.sendMessageEnd(),
                }),
                takeUntil(events.on(conversationEvents.select)),
              ),
          ),
        ),

        sendFakeMessage$: events.on(sendingEvents.sendMessage).pipe(
          map(({ payload }) => payload),
          filter(({ conversation }) => conversation.allow_instant_messages),
          map(({ payload, conversation, fakeMessages }) =>
            sendingEvents.sendFakeMessageSuccess({ payload, fakeMessages, conversation }),
          ),
        ),

        onSendRealMessageSuccess$: events.on(sendingEvents.sendRealMessageSuccess).pipe(
          map(({ payload }) => payload),
          tap(({ response, hadFake }) => {
            if (!hadFake) {
              dispatch.sendMessageSuccess({ conversation: store.conversation()!, messages: response.messages })
            }
          }),
        ),

        onSendFakeMessageSuccess$: events.on(sendingEvents.sendFakeMessageSuccess).pipe(
          map(({ payload }) => payload),
          map(({ conversation, payload, fakeMessages }) => {
            const cost = messagingService.messageCost(conversation, payload, true)
            if (cost) {
              creditsStore.onFakeCreditsSpent(cost)
            }
            return sendingEvents.sendMessageSuccess({ conversation: store.conversation()!, messages: fakeMessages })
          }),
        ),

        onSendMessageSuccess$: events.on(sendingEvents.sendMessageSuccess).pipe(
          map(({ payload }) => payload),
          tap(({ conversation, messages }) => {
            const photoMessage = messages.find(message => message.type === 'photo')
            if (photoMessage) {
              popoverService.show('nc-check-circle', photoMessage.status === 'pending' ? 'Uploaded' : 'Sent')
            }

            messageCacheService.removeCachedMessage(conversation.profile, messages)
          }),
        ),

        onSendMessageFailure$: events.on(sendingEvents.sendMessageFailure).pipe(
          map(({ payload }) => payload),
          tap(({ error, payload, profile_id }) => {
            if (isPaymentRequired(error)) {
              if (payload.request_or_share_access) {
                void accessService.requestWithoutPrompt(profile_id, 'message-input-request-access-checkbox')
              }
              return creditsNavigationService.visitCredits('sendMessage', { source, profile_id })
            }

            if (!error?.error?.fieldErrors) {
              defaultErrorHandler(error)
            }
          }),
        ),
      }),
    ),
  )
